// SPDX-License-Identifier: LicenseRef-Proprietary

import { useState, useEffect, useRef } from 'react';
import { pubmedSearch, type PubmedPaperSummary } from '../../../../../services/pubmedService';
import { SEARCH_DEBOUNCE_DELAY_MS, MIN_SEARCH_QUERY_LENGTH } from '../constants';

export interface UseDebouncedSearchResult {
  searchQuery: string;
  searchResults: PubmedPaperSummary[];
  isSearching: boolean;
  searchError: string | null;
  setSearchQuery: (query: string) => void;
}

export function useDebouncedSearch(): UseDebouncedSearchResult {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PubmedPaperSummary[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);
  const currentQueryRef = useRef<string>('');

  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    currentQueryRef.current = trimmedQuery;

    // Clear results if query is empty
    if (trimmedQuery.length === 0) {
      setSearchResults([]);
      setSearchError(null);
      setIsSearching(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    // Minimum query length: don't search until >= MIN_SEARCH_QUERY_LENGTH characters
    if (trimmedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      setSearchResults([]);
      setSearchError(null);
      setIsSearching(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    // Debounce: wait SEARCH_DEBOUNCE_DELAY_MS before searching
    const timeoutId = setTimeout(() => {
      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Increment request ID for stale response detection
      const currentRequestId = ++requestIdRef.current;
      const queryAtRequestTime = trimmedQuery;

      setIsSearching(true);
      setSearchError(null);

      pubmedSearch(
        {
          query: queryAtRequestTime,
          page: 0,
          pageSize: 20,
        },
        controller.signal
      )
        .then((response) => {
          // Check if this response is stale
          if (currentRequestId !== requestIdRef.current) {
            return; // Ignore stale response
          }
          if (currentQueryRef.current !== queryAtRequestTime) {
            return; // Query changed, ignore this response
          }

          // Check if request was aborted
          if (controller.signal.aborted) {
            return;
          }

          setSearchResults(response.data.items);
          setIsSearching(false);
        })
        .catch((error) => {
          // Ignore abort errors silently
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }

          // Check if this error is for a stale request
          if (currentRequestId !== requestIdRef.current) {
            return;
          }
          if (currentQueryRef.current !== queryAtRequestTime) {
            return;
          }

          setIsSearching(false);
          setSearchError(error instanceof Error ? error.message : 'Search failed');
        });
    }, SEARCH_DEBOUNCE_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [searchQuery]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    searchQuery,
    searchResults,
    isSearching,
    searchError,
    setSearchQuery,
  };
}

